import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { adminJson } from '../utils/adminAuth';

const FIELDS = [
  ['dealership_name', 'Dealership name'], ['dba_name', 'DBA name'],
  ['street_address', 'Street address'], ['city', 'City'], ['state', 'State'], ['zip_code', 'ZIP'],
  ['phone', 'Phone'], ['website', 'Website'],
  ['primary_contact_name', 'Contact name'], ['primary_contact_email', 'Contact email'],
  ['primary_contact_phone', 'Contact phone'], ['dms_system', 'DMS system'],
];
// photo_aspect_ratio is rendered as a dropdown (not a text FIELD) and must never
// be blank — the DB CHECK only allows '16:9' | '4:3'. Default new dealers to 4:3.
const blankForm = { ...Object.fromEntries(FIELDS.map(([k]) => [k, ''])), photo_aspect_ratio: '16:9' };

export default function AdminDealers() {
  const navigate = useNavigate();
  const [dealers, setDealers] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(null);   // null | 'new' | dealer
  // Which dealer the modal is showing NOW: a slow detail GET for a dealer the admin already
  // closed must not land on the next one (it would stamp A's listing settings onto B).
  const editingId = useRef(null);
  const [form, setForm] = useState(blankForm);
  const [sftpFor, setSftpFor] = useState(null);
  const [sftpForm, setSftpForm] = useState({ username: '', password: '' });
  // Listing photos: the dealer's display order + Show damage photos (saved by their own
  // endpoint after the main Save). `orig` is what the server has, so only a change is sent.
  const [orders, setOrders] = useState([]);
  const [disp, setDisp] = useState({ display_order_id: null, show_damage_photos: false, orig: null });
  const logoInput = useRef(null);
  const logoTarget = useRef(null);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('adminAuth');
    if (!token) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);
  const flash = (m) => { setMessage(m); setTimeout(() => setMessage(''), 2800); };

  const load = useCallback(() => {
    const h = authHeader();
    if (!h) return;
    setLoading(true);
    const qs = new URLSearchParams();
    if (search.trim()) qs.append('search', search.trim());
    if (typeFilter) qs.append('dealer_type', typeFilter);
    qs.append('limit', '500');
    fetch(`${API_BASE}/admin/dealers?${qs}`, { headers: h })
      .then(adminJson)
      .then(d => setDealers(Array.isArray(d) ? d : (d.dealers || d.dealerships || [])))
      .catch(() => setError('Failed to load dealers'))
      .finally(() => setLoading(false));
  }, [authHeader, search, typeFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const isDeleted = (d) => !!(d.is_deleted || d.deleted || d.deleted_at);

  const [resolving, setResolving] = useState(false);
  const openAdd = () => { setForm(blankForm); setEditing('new'); editingId.current = 'new'; };
  const openEdit = async (d) => {
    // Seed from the list row, then refresh with FULL details — the list endpoint
    // omits address/phone/website, so without this the form would show blanks and
    // a Save could wipe saved values.
    setForm({ ...blankForm, ...Object.fromEntries(FIELDS.map(([k]) => [k, d[k] ?? ''])), photo_aspect_ratio: d.photo_aspect_ratio || '16:9' });
    setEditing(d);
    editingId.current = d.id;
    setDisp({ display_order_id: null, show_damage_photos: false, orig: null });
    const h = authHeader();
    if (!h) return;
    fetch(`${API_BASE}/admin/display-orders`, { headers: h }).then(adminJson)
      .then(o => setOrders(Array.isArray(o) ? o : [])).catch(() => setOrders([]));
    try {
      const res = await fetch(`${API_BASE}/admin/dealers/${d.id}`, { headers: h });
      if (res.ok) {
        const full = await res.json();
        if (editingId.current !== d.id) return;
        setForm(f => ({ ...f, ...Object.fromEntries(FIELDS.map(([k]) => [k, full[k] ?? ''])), photo_aspect_ratio: full.photo_aspect_ratio || '16:9' }));
        const cur = { display_order_id: full.display_order_id ?? null, show_damage_photos: !!full.show_damage_photos };
        setDisp({ ...cur, orig: cur });
      }
    } catch { /* keep list-seeded form */ }
  };

  // Kim enters/corrects the website → server pulls address+phone(+coords) from the
  // site and saves them; we fill the form so she can review.
  const resolveWebsite = async () => {
    if (!editing || editing === 'new') return;
    if (!form.website.trim()) { setError('Enter a website URL first'); return; }
    const h = authHeader(); if (!h) return;
    setResolving(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/admin/dealers/${editing.id}/resolve-website`, {
        method: 'POST', headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify({ website: form.website.trim() }),
      });
      const d = await res.json();
      if (!res.ok) { setError(d.detail || 'Lookup failed'); return; }
      const dl = d.dealer || {};
      setForm(f => ({
        ...f,
        website: dl.website ?? f.website,
        street_address: dl.street_address ?? f.street_address,
        city: dl.city ?? f.city, state: dl.state ?? f.state,
        zip_code: dl.zip_code ?? f.zip_code, phone: dl.phone ?? f.phone,
      }));
      if (d.source) flash(d.message || 'Populated ✓');
      else setError(d.message || 'No match — check the dealer name/URL or enter fields manually.');
      load();
    } catch (e) { setError('Lookup failed'); } finally { setResolving(false); }
  };

  const save = async () => {
    const h = authHeader();
    if (!h) return;
    if (!form.dealership_name.trim()) { setError('Dealership name is required'); return; }
    const isNew = editing === 'new';
    try {
      const res = await fetch(isNew ? `${API_BASE}/admin/dealers` : `${API_BASE}/admin/dealers/${editing.id}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); setError(e.detail || 'Save failed'); return; }
      let note = '';
      if (!isNew && disp.orig && (disp.display_order_id !== disp.orig.display_order_id
                                  || disp.show_damage_photos !== disp.orig.show_damage_photos)) {
        const r2 = await fetch(`${API_BASE}/admin/display-orders/dealers/${editing.id}`, {
          method: 'PUT', headers: { ...h, 'Content-Type': 'application/json' },
          body: JSON.stringify({ display_order_id: disp.display_order_id, show_damage_photos: disp.show_damage_photos }),
        });
        const d2 = await r2.json().catch(() => ({}));
        if (!r2.ok) { setError(`Dealer saved, but listing photo settings were not: ${d2.detail || 'error'}`); load(); return; }
        if (d2.show_damage_photos && !d2.damage_effective) note = ` Damage stays hidden: "${d2.effective_order_name}" has no damage position.`;
        if (d2.resort) note += ` ${d2.resort.reordered} car(s) re-sorted.`;
      }
      setEditing(null); flash((isNew ? 'Dealer created' : 'Dealer updated') + note); load();
    } catch (e) { setError('Save failed'); }
  };

  const act = async (d, path, method, okMsg, q) => {
    const h = authHeader();
    if (!h) return;
    const res = await fetch(`${API_BASE}/admin/dealers/${d.id}${path}${q || ''}`, { method, headers: h });
    if (res.ok) { flash(okMsg); load(); if (editing && editing.id === d.id) setEditing(null); }
    else { const e = await res.json().catch(() => ({})); setError(e.detail || 'Action failed'); }
  };

  const enableSftp = async () => {
    const h = authHeader();
    if (!h || !sftpFor) return;
    if (!sftpForm.username || !sftpForm.password) { setError('Username and password required'); return; }
    const res = await fetch(`${API_BASE}/admin/dealers/${sftpFor.id}/sftp`, {
      method: 'POST', headers: { ...h, 'Content-Type': 'application/json' }, body: JSON.stringify(sftpForm),
    });
    if (res.ok) { flash('SFTP enabled'); setSftpFor(null); setSftpForm({ username: '', password: '' }); load(); }
    else { const e = await res.json().catch(() => ({})); setError(e.detail || 'SFTP setup failed'); }
  };

  const pickLogo = (d) => { logoTarget.current = d; logoInput.current?.click(); };
  const onLogo = async (e) => {
    const f = e.target.files?.[0]; const d = logoTarget.current;
    e.target.value = '';
    if (!f || !d) return;
    const h = authHeader();
    if (!h) return;
    const fd = new FormData(); fd.append('file', f);
    const res = await fetch(`${API_BASE}/admin/dealers/${d.id}/logo`, { method: 'POST', headers: h, body: fd });
    if (res.ok) flash('Logo uploaded'); else setError('Logo upload failed');
  };

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      <input ref={logoInput} type="file" accept="image/*" onChange={onLogo} style={{ display: 'none' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Dealers</h2>
      </div>
      {error && <div style={{ color: '#dc2626', marginBottom: 8 }}>{error} <button onClick={() => setError('')} style={linkBtn}>dismiss</button></div>}
      {message && <div style={{ color: '#059669', marginBottom: 8 }}>{message}</div>}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', margin: '12px 0' }}>
        <input placeholder="Search name / contact…" value={search} onChange={e => setSearch(e.target.value)} style={ctrl} />
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={ctrl}>
          <option value="">All types</option><option value="parent">Parent</option><option value="child">Child</option>
        </select>
        <button onClick={openAdd} style={primaryBtn}>+ Add Dealer</button>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{dealers.length} dealer(s)</span>
      </div>

      {loading ? <div>Loading…</div> : (
        <table style={table}>
          <thead><tr>{['Dealership', 'Contact', 'Location', 'Status', 'Actions'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {dealers.map(d => (
              <tr key={d.id} style={{ borderBottom: '1px solid #eef2f7', opacity: isDeleted(d) ? 0.6 : 1 }}>
                <td style={td}><div style={{ fontWeight: 600 }}>{d.dealership_name}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>{d.dealer_code || ''}{d.dealer_type ? ` · ${d.dealer_type}` : ''}</div></td>
                <td style={td}>{d.primary_contact_name || '—'}<div style={{ fontSize: 12, color: '#6b7280' }}>{d.primary_contact_email || ''}</div></td>
                <td style={td}>{[d.city, d.state].filter(Boolean).join(', ') || '—'}</td>
                <td style={td}>{isDeleted(d) ? <span style={{ color: '#dc2626' }}>deleted</span> : <span style={{ color: '#059669' }}>{d.account_status || 'active'}</span>}</td>
                <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 13 }}>
                  <button onClick={() => openEdit(d)} style={linkBtn}>Edit</button>{' · '}
                  <button onClick={() => { setSftpFor(d); setSftpForm({ username: '', password: '' }); }} style={linkBtn}>SFTP</button>{' · '}
                  <button onClick={() => window.confirm(`Send welcome email to ${d.primary_contact_email}?`) && act(d, '/welcome-email', 'POST', 'Welcome email sent')} style={linkBtn}>Email</button>{' · '}
                  <button onClick={() => pickLogo(d)} style={linkBtn}>Logo</button>{' · '}
                  {isDeleted(d)
                    ? <button onClick={() => act(d, '/restore', 'POST', 'Dealer restored')} style={{ ...linkBtn, color: '#059669' }}>Restore</button>
                    : <button onClick={() => window.confirm(`Deactivate ${d.dealership_name}?`) && act(d, '', 'DELETE', 'Dealer deactivated')} style={{ ...linkBtn, color: '#dc2626' }}>Deactivate</button>}
                </td>
              </tr>
            ))}
            {!dealers.length && <tr><td colSpan={5} style={{ ...td, color: '#6b7280' }}>No dealers.</td></tr>}
          </tbody>
        </table>
      )}

      {/* Add/Edit modal */}
      {editing && (
        <div style={overlay} onClick={() => setEditing(null)}>
          <div style={{ ...modal, width: 560 }} onClick={e => e.stopPropagation()}>
            <h3>{editing === 'new' ? 'Add Dealer' : `Edit ${editing.dealership_name}`}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, maxHeight: '60vh', overflowY: 'auto' }}>
              {FIELDS.map(([k, label]) => (
                <label key={k} style={lbl}>{label}
                  <input value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} style={inp} />
                </label>
              ))}
              <label style={lbl}>Photo aspect ratio
                <select value={form.photo_aspect_ratio || '16:9'} onChange={e => setForm({ ...form, photo_aspect_ratio: e.target.value })} style={inp}>
                  <option value="4:3">4:3 (Standard)</option>
                  <option value="16:9">16:9 (Widescreen)</option>
                </select>
              </label>
            </div>
            {editing !== 'new' && disp.orig && (() => {
              const def = orders.find(o => o.is_default);
              // An explicit pin to the default shows (and saves) as Default; the server stores NULL.
              const pinned = disp.display_order_id != null && !(def && def.id === disp.display_order_id)
                ? disp.display_order_id : null;
              const eff = pinned == null ? def : orders.find(o => o.id === pinned);
              return (
                <div style={{ marginTop: 10, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Listing photos</div>
                  <label style={lbl}>Display order
                    <select value={pinned ?? ''} style={inp}
                            onChange={e => setDisp({ ...disp, display_order_id: e.target.value === '' ? null : Number(e.target.value) })}>
                      <option value="">Default{def ? ` (${def.name})` : ''}</option>
                      {orders.filter(o => !o.is_default).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  </label>
                  <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
                    <input type="checkbox" checked={disp.show_damage_photos}
                           onChange={e => setDisp({ ...disp, show_damage_photos: e.target.checked })} />
                    Show damage photos on listings
                  </label>
                  {disp.show_damage_photos && eff && !eff.has_damage && (
                    <div style={{ color: '#9a3412', fontSize: 12, marginTop: 4 }}>
                      Damage stays hidden: "{eff.name}" has no damage position. Add one on the Display Orders page,
                      or pick an order that places damage.</div>)}
                </div>);
            })()}
            {editing !== 'new' && (
              <div style={{ marginTop: 10, padding: '8px 10px', background: '#f0f7ff', border: '1px solid #cfe3ff', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, color: '#345' }}>Enter the correct website above, then:</span>
                <button onClick={resolveWebsite} disabled={resolving} style={primaryBtn}>
                  {resolving ? 'Looking up…' : '🔎 Look up & populate location/contact'}
                </button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center' }}>
              <button onClick={save} style={primaryBtn}>Save</button>
              <button onClick={() => setEditing(null)} style={btn}>Cancel</button>
              {editing !== 'new' && <>
                <div style={{ flex: 1 }} />
                <button onClick={() => act(editing, '/toggle-delete-access', 'POST', 'Delete-access toggled')} style={linkBtn} title="Admin protection flag">Toggle delete-access</button>
                <button onClick={() => window.confirm('PERMANENTLY delete this dealer? This cannot be undone.') && act(editing, '', 'DELETE', 'Dealer permanently deleted', '?permanent=true')} style={{ ...linkBtn, color: '#dc2626' }}>Delete permanently</button>
              </>}
            </div>
          </div>
        </div>
      )}

      {/* SFTP modal */}
      {sftpFor && (
        <div style={overlay} onClick={() => setSftpFor(null)}>
          <div style={modal} onClick={e => e.stopPropagation()}>
            <h3>Enable SFTP — {sftpFor.dealership_name}</h3>
            <label style={lbl}>Username<input value={sftpForm.username} onChange={e => setSftpForm({ ...sftpForm, username: e.target.value })} style={inp} /></label>
            <label style={lbl}>Password<input value={sftpForm.password} onChange={e => setSftpForm({ ...sftpForm, password: e.target.value })} style={inp} type="text" /></label>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button onClick={enableSftp} style={primaryBtn}>Enable SFTP</button>
              <button onClick={() => setSftpFor(null)} style={btn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ctrl = { padding: 8, border: '1px solid #d1d5db', borderRadius: 6 };
const btn = { padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' };
const primaryBtn = { ...btn, background: '#0078D4', color: '#fff', border: 'none' };
const linkBtn = { background: 'none', border: 'none', color: '#0078D4', cursor: 'pointer', padding: 0 };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 14 };
const th = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: '8px 10px', verticalAlign: 'top' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 };
const modal = { background: '#fff', borderRadius: 10, padding: 22, width: 420, maxWidth: '94vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' };
const lbl = { display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 };
const inp = { width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box', marginTop: 2 };
